import { 
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  DocumentReference,
  DocumentSnapshot,
  serverTimestamp,
  deleteField,
  collection,
  addDoc,
  getDocs,
  runTransaction,
} from 'firebase/firestore';
import { auth, db } from '../shared/storage/firebase';
import { User, Muse, Squad, LiveSession } from '../types';
import { captureCurrentLocation } from '../shared/utils/location';
import { getCurriculumAdapterById } from '../domains/curriculum/adapters/registry';
import { GAME_REGISTRY } from '../domains/games/models/GameRegistry';
import { toDateSafe } from '../shared/utils/firestoreDates';

/**
 * Derive the list of gameIds required for a given curriculum + level.
 * - Uses the curriculum adapter so it works for any curriculum (music, ai, ai-fall-2025, etc.)
 * - Collects gameIds from both missions and sideQuests when present.
 */
function deriveRequiredGamesForLevel(curriculumId: string | undefined, levelId: string): string[] {
  const adapter = getCurriculumAdapterById(curriculumId || 'music');
  const level = adapter.getLevelById(levelId);
  if (!level) return [];

  const collectFrom = (items?: any[]): string[] => {
    if (!Array.isArray(items)) return [];
    return items
      .map((m: any) => (m && typeof m === 'object' && typeof m.gameId === 'string' ? m.gameId as string : null))
      .filter((id): id is string => !!id);
  };

  const missionGames = collectFrom(level.missions as any[]);
  const sideQuestGames = collectFrom(level.sideQuests as any[]);

  const result = new Set<string>();
  [...missionGames, ...sideQuestGames].forEach(id => {
    if (GAME_REGISTRY[id]) {
      result.add(id);
    }
  });

  return Array.from(result);
}

export class FirebaseService {
  static async createUser(email: string, password: string): Promise<FirebaseUser> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  static async signInUser(email: string, password: string): Promise<FirebaseUser> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  static async signOutUser(): Promise<void> {
    await signOut(auth);
  }

  static async createUserData(uid: string, userData: User): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await setDoc(userRef, userData);
  }

  static async getUserData(uid: string): Promise<User | null> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    const docSnap: DocumentSnapshot = await getDoc(userRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as User;
    }
    
    return null;
  }

  static async updateUserData(uid: string, data: Partial<User>): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, data);
  }

  static async addMuseToUser(uid: string, muse: Muse): Promise<void> {
    await this.addMusesToUser(uid, [muse]);
  }

  /**
   * Safely adds multiple muses to a user's collection without downgrading existing upgraded muses
   */
  static async addMusesToUser(uid: string, newMuses: Muse[]): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);

    // Use transaction to safely merge muses without downgrading
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const currentUserData = userDoc.data();
      const existingMuses = currentUserData.muses || [];

      // Create a map of existing muses by name for quick lookup
      const existingMuseMap = new Map();
      existingMuses.forEach((muse: Muse) => {
        existingMuseMap.set(muse.name, muse);
      });

      // For each new muse, either add it or preserve the higher tier
      const updatedMuses = [...existingMuses];
      newMuses.forEach((newMuse: Muse) => {
        const existingMuse = existingMuseMap.get(newMuse.name);

        if (!existingMuse) {
          // New muse - add it
          updatedMuses.push(newMuse);
          existingMuseMap.set(newMuse.name, newMuse);
        } else {
          // Existing muse - preserve the higher tier
          const existingTier = existingMuse.tier || 1;
          const newTier = newMuse.tier || 1;

          if (newTier > existingTier) {
            // Replace with higher tier version
            const index = updatedMuses.findIndex((m: Muse) => m.name === newMuse.name);
            if (index !== -1) {
              updatedMuses[index] = newMuse;
            }
          }
          // If existing tier is higher or equal, do nothing (preserve existing)
        }
      });

      transaction.update(userRef, { muses: updatedMuses });
    });
  }

  static async updateSelectedMuse(uid: string, muse: Muse): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, { selectedMuse: muse });
  }

  static async updateMuseCash(uid: string, amount: number): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, { museCash: amount });
  }

  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  // Level and Mission Management Functions
  static async startLevel(squadId: string, levelId: string, teacherId: string, teacherName: string) {
    const squadRef = doc(db, 'squads', squadId);
    const squadSnap = await getDoc(squadRef);
    
    if (!squadSnap.exists()) {
      throw new Error('Squad not found');
    }

    const squadData = squadSnap.data() as Squad;
    const assignedSlot = squadData.sessionPlan?.slots.find(slot => slot.levelId === levelId);
    const curriculumId = assignedSlot?.curriculumId || squadData.curriculumId;
    const isAICurriculum = !!curriculumId?.toLowerCase().startsWith('ai');

    // Derive required games for this level from the active curriculum
    const requiredGameIds = deriveRequiredGamesForLevel(curriculumId, assignedSlot?.sourceLevelId || levelId);
    const allGameIds = Object.keys(GAME_REGISTRY);
    // If no explicit games are defined for this level, default to all games
    const unlockedForSession = requiredGameIds.length > 0 ? requiredGameIds : allGameIds;

    const newSessionId = doc(collection(db, '_')).id; // Generate unique ID shared with creativeSession
    
    // Capture current location
    console.log('📍 Capturing teacher location for session start...');
    const locationData = await captureCurrentLocation();
    
    if (locationData.error) {
      console.warn('⚠️ Location capture failed:', locationData.error);
    } else {
      console.log('✅ Location captured successfully:', {
        lat: locationData.latitude,
        lng: locationData.longitude,
        accuracy: locationData.accuracy
      });
    }

    await updateDoc(squadRef, {
      classStatus: 'current',
      currentLessonId: levelId,
      missionProgress: squadData.sessionPlan?.slots.some(slot => slot.curriculumId)
        ? { ...(squadData.missionProgress || {}), [levelId]: {} }
        : {}, // Keep previous sessions' progress for mixed curriculum plans
      unlockedGames: unlockedForSession,
      liveSession: {
        teacherId,
        teacherName,
        startTime: serverTimestamp(),
        sessionId: newSessionId,
        location: locationData
      }
    });

    // Auto-create a creativeSession for AI curricula, 1:1 with this live session
    if (isAICurriculum) {
      try {
        const creativeRef = doc(db, 'creativeSessions', newSessionId);
        await setDoc(creativeRef, {
          squadId,
          teacherId,
          type: 'prompting',
          startTime: serverTimestamp() as any,
          status: 'active',
        }, { merge: true });
        console.log('📝 Creative session created for AI squad', { squadId, newSessionId });
      } catch (e) {
        console.error('Failed to create creative session for AI squad', e);
      }
    }
  }

  static async finishClass(squadId: string) {
    const squadRef = doc(db, 'squads', squadId);
    const squadDoc = await getDoc(squadRef);
    
    if (!squadDoc.exists()) {
      throw new Error('Squad not found');
    }

    const squadData = squadDoc.data() as Squad;
    const currentLevelId = squadData.currentLessonId || '1';
    
    // Save live session to historical collection before finishing
    if (squadData.liveSession) {
      console.log('💾 Saving live session to historical collection...');

      // Preserve the roster that belonged to this lesson. Team membership may
      // change later, so historical photo checks must not use the current teams.
      const teamsSnapshot = await getDocs(collection(db, 'squads', squadId, 'teams'));
      const teamRosterSnapshot = Object.fromEntries(teamsSnapshot.docs
        .map((teamDoc) => {
          const team = teamDoc.data();
          return [teamDoc.id, {
            name: String(team.name || teamDoc.id),
            memberIds: Array.isArray(team.memberIds) ? Array.from(new Set(team.memberIds.map(String))) : [],
          }];
        }));
      
      const endTime = new Date();
      const startTime = toDateSafe(squadData.liveSession.startTime);
      const duration = startTime
        ? Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))
        : 0; // minutes (fallback for legacy/malformed data)
      
      const historicalSession: Omit<LiveSession, 'id'> = {
        sessionId: squadData.liveSession.sessionId,
        squadId: squadId, // Use the squadId parameter instead of squadData.id
        squadTitle: squadData.title,
        teacherId: squadData.liveSession.teacherId,
        teacherName: squadData.liveSession.teacherName,
        startTime: squadData.liveSession.startTime,
        endTime: serverTimestamp() as any,
        duration,
        location: squadData.liveSession.location,
        attendanceData: squadData.attendance || {},
        teamRosterSnapshot,
        levelId: currentLevelId,
        levelTitle: `Level ${currentLevelId}` // Could be enhanced with actual level titles
      };
      
      // Save to liveSessions collection
      await addDoc(collection(db, 'liveSessions'), historicalSession);
      console.log('✅ Live session saved to historical collection');

      // If this was an AI curriculum squad, mark the corresponding creativeSession as archived
      const isAICurriculum = (squadData.sessionPlan?.slots.find(slot => slot.levelId === currentLevelId)?.curriculumId || squadData.curriculumId || '').toLowerCase().startsWith('ai');
      const creativeSessionId = squadData.liveSession.sessionId;

      if (isAICurriculum && creativeSessionId) {
        try {
          const creativeRef = doc(db, 'creativeSessions', creativeSessionId);
          await updateDoc(creativeRef, {
            status: 'archived',
            endTime: serverTimestamp() as any,
          });
          console.log('📝 Creative session archived for AI squad', { squadId, creativeSessionId });
        } catch (e) {
          console.error('Failed to archive creative session for AI squad', e);
        }
      }
    }
    
    // Reset all students to inactive for next class
    const resetAttendance: Record<string, boolean> = {};
    if (squadData.studentIds) {
      squadData.studentIds.forEach((studentId: string) => {
        resetAttendance[studentId] = false;
      });
    }

    // Mark current level as completed
    const completedLevels = squadData.completedLevels || [];
    const updatedCompletedLevels = completedLevels.includes(currentLevelId) 
      ? completedLevels 
      : [...completedLevels, currentLevelId];

    // Finish class but stay on the same level - don't auto-advance
    await updateDoc(squadRef, {
      classStatus: 'next',
      completedLevels: updatedCompletedLevels,
      attendance: resetAttendance,
      lastAttendanceUpdate: new Date(),
      liveSession: deleteField(),
      attendanceMetadata: deleteField()
    });
  }

  /**
   * Soft-cancel a live class session that was started by mistake.
   * - Writes a historical liveSessions doc with status 'canceled' and cancel metadata
   * - Resets the squad's liveSession / attendance / missionProgress so the class is no longer live
   */
  static async cancelClass(
    squadId: string,
    options: {
      teacherId: string;
      reason: 'accidental' | 'otherTeacher' | 'preview' | 'other';
      reasonOther?: string;
    }
  ) {
    const squadRef = doc(db, 'squads', squadId);
    const squadDoc = await getDoc(squadRef);

    if (!squadDoc.exists()) {
      throw new Error('Squad not found');
    }

    const squadData = squadDoc.data() as Squad;
    const currentLevelId = squadData.currentLessonId || '1';

    if (squadData.liveSession) {
      console.log('💾 Recording canceled live session...');

      const endTime = new Date();
      const startTime = toDateSafe(squadData.liveSession.startTime);
      const duration = startTime
        ? Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))
        : 0; // minutes (fallback for legacy/malformed data)

      const canceledSessionBase: Omit<LiveSession, 'id'> = {
        sessionId: squadData.liveSession.sessionId,
        squadId,
        squadTitle: squadData.title,
        teacherId: squadData.liveSession.teacherId,
        teacherName: squadData.liveSession.teacherName,
        startTime: squadData.liveSession.startTime,
        endTime: serverTimestamp() as any,
        duration,
        location: squadData.liveSession.location,
        attendanceData: squadData.attendance || {},
        levelId: currentLevelId,
        levelTitle: `Level ${currentLevelId}`,
        status: 'canceled',
        canceledAt: serverTimestamp() as any,
        canceledByTeacherId: options.teacherId,
        cancelReason: options.reason,
      };

      const canceledSession: Omit<LiveSession, 'id'> = {
        ...canceledSessionBase,
        ...(options.reason === 'other'
          ? { cancelReasonOther: options.reasonOther || null }
          : {}),
      };

      await addDoc(collection(db, 'liveSessions'), canceledSession);
      console.log('✅ Canceled live session recorded');

      // If this was an AI curriculum squad, mark the corresponding creativeSession as canceled
      const isAICurriculum = (squadData.sessionPlan?.slots.find(slot => slot.levelId === currentLevelId)?.curriculumId || squadData.curriculumId || '').toLowerCase().startsWith('ai');
      const creativeSessionId = squadData.liveSession.sessionId;

      if (isAICurriculum && creativeSessionId) {
        try {
          const creativeRef = doc(db, 'creativeSessions', creativeSessionId);
          await updateDoc(creativeRef, {
            status: 'canceled',
            endTime: serverTimestamp() as any,
          });
          console.log('📝 Creative session canceled for AI squad', { squadId, creativeSessionId });
        } catch (e) {
          console.error('Failed to cancel creative session for AI squad', e);
        }
      }
    }

    // Reset all students to inactive and clear attendance metadata
    const resetAttendance: Record<string, boolean> = {};
    if (squadData.studentIds) {
      squadData.studentIds.forEach((studentId: string) => {
        resetAttendance[studentId] = false;
      });
    }

    await updateDoc(squadRef, {
      classStatus: 'next',
      attendance: resetAttendance,
      lastAttendanceUpdate: new Date(),
      missionProgress: squadData.sessionPlan?.slots.some(slot => slot.curriculumId)
        ? { ...(squadData.missionProgress || {}), [currentLevelId]: {} }
        : {}, // Clear this false start without erasing earlier sessions
      liveSession: deleteField(),
      attendanceMetadata: deleteField()
    });
  }

  static async advanceToNextLevel(squadId: string) {
    const squadRef = doc(db, 'squads', squadId);
    const squadDoc = await getDoc(squadRef);
    
    if (!squadDoc.exists()) {
      throw new Error('Squad not found');
    }

    const squadData = squadDoc.data();
    const currentLevelId = squadData.currentLessonId || '1';
    
    // Calculate next level
    const currentLevelNum = parseInt(currentLevelId);
    const plannedSlots = squadData.sessionPlan?.slots as NonNullable<Squad['sessionPlan']>['slots'] | undefined;
    const plannedIndex = plannedSlots?.findIndex(slot => slot.levelId === currentLevelId) ?? -1;
    const nextLevelId = plannedIndex >= 0
      ? plannedSlots?.[plannedIndex + 1]?.levelId || currentLevelId
      : (currentLevelNum + 1).toString();
    
    await updateDoc(squadRef, {
      currentLessonId: nextLevelId,
      levelAdvancedAt: new Date()
    });
  }

  static async updateMissionProgress(
    squadId: string, 
    levelId: string, 
    missionId: string, 
    action: 'toggle' | 'increment' | 'set',
    value?: number
  ) {
    const squadRef = doc(db, 'squads', squadId);
    const squadDoc = await getDoc(squadRef);
    
    if (!squadDoc.exists()) {
      throw new Error('Squad not found');
    }

    const squadData = squadDoc.data();
    const missionProgress = squadData.missionProgress || {};
    const levelProgress = missionProgress[levelId] || {};
    
    if (action === 'toggle') {
      // Toggle completion status for completion missions
      levelProgress[missionId] = !levelProgress[missionId];
    } else if (action === 'increment') {
      // Increment progress for measurable missions
      const currentValue = levelProgress[missionId] || 0;
      levelProgress[missionId] = currentValue + (value || 1);
    } else if (action === 'set') {
      // Set specific value for measurable missions
      levelProgress[missionId] = value || 0;
    }

    missionProgress[levelId] = levelProgress;

    await updateDoc(squadRef, {
      missionProgress: missionProgress,
      lastMissionUpdate: new Date()
    });
  }
}

// Standalone functions for easy import
export const startLevel = async (squadId: string, levelId: string, teacherId: string, teacherName: string) => {
  return FirebaseService.startLevel(squadId, levelId, teacherId, teacherName);
};

export const finishClass = async (squadId: string) => {
  return FirebaseService.finishClass(squadId);
};

export const cancelClass = async (
  squadId: string,
  options: {
    teacherId: string;
    reason: 'accidental' | 'otherTeacher' | 'preview' | 'other';
    reasonOther?: string;
  }
) => {
  return FirebaseService.cancelClass(squadId, options);
};

export const advanceToNextLevel = async (squadId: string) => {
  return FirebaseService.advanceToNextLevel(squadId);
};

export const updateMissionProgress = async (
  squadId: string, 
  levelId: string, 
  missionId: string, 
  action: 'toggle' | 'increment' | 'set',
  value?: number
) => {
  return FirebaseService.updateMissionProgress(squadId, levelId, missionId, action, value);
}; 
